/**
 * Scrolling camera.
 *
 * ## Why this feels the way it does
 *
 * **The camera only ever sits on whole pixels.** This is the whole ballgame for
 * a tile-based game. The renderer blits at `floor(worldX - camera.x)`, so a
 * camera carrying a fractional position means that a player creeping forward by
 * 0.3px can flip the floor result and shift the entire visible world by a
 * pixel. Every small input makes the whole screen judder. Keeping the camera
 * integral means the world is rock steady and the player is the only thing that
 * moves, which is exactly how the 16-bit games look.
 *
 * **It follows with a deadzone, not a lerp.** The camera sits still until the
 * follow point leaves a box around the middle of the screen, then moves just
 * enough to put that point back at the edge of that box. Two reasons:
 *
 *  - It is what the originals do, and it is why Tavi's screen does not slide
 *    around while he walks.
 *  - Smoothing (a lerp toward the target) is actively wrong here. Combined with
 *    integer snapping it produces a camera that creeps in sub-pixel increments
 *    and snaps back to integers, which judders *more* than no smoothing at all.
 *    A deadzone moves the camera in clean, whole-pixel jumps.
 *
 * Vertical follow uses a deadzone too, and biases the player slightly below
 * centre so there is more room to see what is coming than what is behind.
 *
 * **Look-ahead shifts the follow point, not the deadzone.** Facing, run speed,
 * and held up/down ease an integer bias onto the player's centre. The deadzone
 * then tracks that biased point, so Tavi sits toward the trailing edge of the
 * screen and more of the course ahead (or above/below) stays in view. Bias
 * eases a few pixels per frame so the view does not snap when you turn around.
 *
 * **The invariant is that the follow point always ends the frame inside the
 * deadzone.** `fitAxis` is what guarantees it: rounding to the nearest whole
 * pixel can leave a point who was exactly on the boundary sitting a fraction
 * outside it, which would make the camera want to correct every single frame
 * and creep a pixel at a time. That creep was the second half of the original
 * "jerky camera" bug, and it is invisible until you know to look for it.
 */

import { TILE, VIEW_H, VIEW_W } from '../engine/constants'

/** Inputs that pull the viewport toward where the player is headed. */
export type CameraLook = {
  /** -1 left, +1 right. No facing means neutral follow. */
  facing?: number
  /** Horizontal velocity in px/frame; scales run look-ahead. */
  vx?: number
  /** Peek upward (more sky / upper platforms). */
  lookUp?: boolean
  /** Peek downward (more ground / pits). */
  lookDown?: boolean
}

/** Base horizontal lead from facing alone (native px). */
export const LOOK_FACE = 36
/** Extra lead at full run speed (native px). */
export const LOOK_RUN = 48
/** Vertical peek when holding up or down (native px). */
export const LOOK_VERT = 48
/** Max pixels the look bias may change per axis per frame. */
export const LOOK_STEP = 3
/** |vx| at which run look-ahead is fully applied (matches runSpeed). */
export const LOOK_RUN_SPEED = 2.5

export class Camera {
  /** Camera position in native pixels. Always an integer. */
  x = 0
  y = 0

  /** Level size in native pixels. */
  levelW = 0
  levelH = 0

  /**
   * Half-width of the horizontal deadzone, in native pixels. The follow point
   * may wander this far either side of the screen centre before the camera
   * moves.
   */
  deadzoneX = 40
  /**
   * Half-height of the vertical deadzone. Generous, because a tight vertical
   * deadzone makes the whole screen bob on every jump.
   */
  deadzoneY = 24

  /**
   * Where the follow point should sit vertically on screen, as a fraction.
   * 0.6 puts them below centre: you see further ahead than behind.
   */
  anchorY = 0.6

  /** Current eased look bias (integer px). Positive X looks right. */
  lookX = 0
  /** Current eased look bias (integer px). Positive Y looks down. */
  lookY = 0

  /** Set the level bounds so the camera never shows outside the course. */
  setBounds(wTiles: number, hTiles: number): void {
    this.levelW = wTiles * TILE
    this.levelH = hTiles * TILE
  }

  /** Drop look bias (e.g. on course load / snap). */
  resetLook(): void {
    this.lookX = 0
    this.lookY = 0
  }

  /**
   * Advance the camera for a player box at this position.
   *
   * `px`/`py` are the hitbox's top-left; only its centre matters. Optional
   * `look` eases the follow point ahead of facing / run / up / down.
   */
  update(
    px: number,
    py: number,
    w: number,
    h: number = 0,
    look?: CameraLook,
  ): void {
    this.easeLook(look)

    const cx = px + w / 2 + this.lookX
    const cy = py + h / 2 + this.lookY

    // Measure the deadzone against the position the camera will *actually*
    // hold, not some unrounded ideal.
    const baseX = Math.round(this.x)
    const baseY = Math.round(this.y)
    const dzx = this.deadzoneX
    const dzy = this.deadzoneY

    // ── horizontal ────────────────────────────────────────────────────
    // The deadzone is a window on the *screen* whose edges are fixed. Once the
    // follow point crosses an edge, the camera moves by exactly the overshoot,
    // so they end up back on the edge rather than being re-centred --
    // re-centring would lurch every time they crossed.
    const centre = baseX + VIEW_W / 2
    const idealX =
      cx < centre - dzx
        ? cx + dzx - VIEW_W / 2
        : cx > centre + dzx
          ? cx - dzx - VIEW_W / 2
          : baseX
    this.x = this.fitAxis(idealX, (v) => {
      const c = v + VIEW_W / 2
      return cx >= c - dzx && cx <= c + dzx
    })

    // ── vertical ──────────────────────────────────────────────────────
    const anchor = baseY + VIEW_H * this.anchorY
    const idealY =
      cy < anchor - dzy
        ? cy + dzy - VIEW_H * this.anchorY
        : cy > anchor + dzy
          ? cy - dzy - VIEW_H * this.anchorY
          : baseY
    this.y = this.fitAxis(idealY, (v) => {
      const a = v + VIEW_H * this.anchorY
      return cy >= a - dzy && cy <= a + dzy
    })

    this.settle()
  }

  /**
   * Ease `lookX`/`lookY` toward the desired bias from facing, speed, and peek.
   *
   * Steps are whole pixels so the follow point never introduces sub-pixel
   * camera motion on its own.
   */
  private easeLook(look?: CameraLook): void {
    const facing = look?.facing === -1 || look?.facing === 1 ? look.facing : 0
    const speed = Math.abs(look?.vx ?? 0)
    const runFrac = Math.min(1, speed / LOOK_RUN_SPEED)
    const desiredX = Math.round(facing * (LOOK_FACE + LOOK_RUN * runFrac))
    const desiredY =
      look?.lookUp === look?.lookDown
        ? 0
        : look?.lookUp
          ? -LOOK_VERT
          : look?.lookDown
            ? LOOK_VERT
            : 0

    this.lookX = stepToward(this.lookX, desiredX, LOOK_STEP)
    this.lookY = stepToward(this.lookY, desiredY, LOOK_STEP)
  }

  /**
   * Snap a camera position to a whole pixel, preferring the nearest one that
   * keeps the follow point inside their deadzone.
   *
   * Rounding to nearest is not always safe here. The ideal position is
   * fractional, and rounding it one way can leave the follow point a fraction
   * of a pixel outside the window. The camera then wants to correct on the
   * very next frame, recomputes the same fractional ideal, rounds the same
   * way, and the point sits permanently on the boundary -- so any movement at
   * all, however small, drags the camera a pixel at a time. That reads as a
   * slow, almost invisible creep, which is exactly the "jerky camera"
   * complaint.
   *
   * So: take the nearest whole pixel; if that fails the containment test, step
   * one pixel the other way.
   */
  private fitAxis(
    ideal: number,
    contains: (cameraValue: number) => boolean,
  ): number {
    const near = Math.round(ideal)
    if (contains(near)) return near
    const other = ideal > near ? near + 1 : near - 1
    return contains(other) ? other : near
  }

  /**
   * Snap to whole pixels and pull back inside the course.
   *
   * Order matters: rounding first means a camera pinned against a level edge
   * still lands on a whole pixel, and clamping after rounding cannot push it
   * back out.
   */
  private settle(): void {
    this.x = Math.round(this.x)
    this.y = Math.round(this.y)
    this.clamp()
  }

  /** Keep the view inside the course. */
  clamp(): void {
    const maxX = Math.max(0, this.levelW - VIEW_W)
    const maxY = Math.max(0, this.levelH - VIEW_H)
    this.x = Math.max(0, Math.min(this.x, maxX))
    this.y = Math.max(0, Math.min(this.y, maxY))
  }

  /**
   * Scroll instantly so a box at this tile position is in view, centred.
   *
   * Look bias is cleared so the next `update` builds it fresh from input.
   */
  snapToTile(tx: number, ty: number, w = 0, h = 0): void {
    this.resetLook()
    this.x = tx * TILE + w / 2 - VIEW_W / 2
    this.y = ty * TILE + h / 2 - VIEW_H * this.anchorY
    this.settle()
  }

  /** Snap so a box at a world position is in view, centred. Used by debug. */
  snapToWorld(x: number, y: number, w = 0, h = 0): void {
    this.resetLook()
    this.x = x + w / 2 - VIEW_W / 2
    this.y = y + h / 2 - VIEW_H * this.anchorY
    this.settle()
  }
}

function stepToward(current: number, target: number, step: number): number {
  if (current === target) return current
  const delta = target - current
  if (Math.abs(delta) <= step) return target
  return current + Math.sign(delta) * step
}
